import { z } from 'zod';

/**
 * Категории уведомлений: сотрудник выбирает, какие приходят на устройства (push) и какие
 * письмом. В колокольчике на сайте видно всё всегда.
 */
const CATEGORIES = {
  approvals: { label: 'Нужно согласовать или выполнить', hint: 'Документ дошёл до вас по маршруту, задача по шагу, контроль' },
  results: { label: 'Ход и итог моих документов', hint: 'Согласовано, отклонено, возвращено на доработку, задача по маршруту выполнена' },
  assignments: { label: 'Поручения и подзадачи', hint: 'Новое поручение, результат на проверку, возврат на доработку, автозадачи' },
  comments: { label: 'Комментарии', hint: 'Новые комментарии в ваших задачах', noEmail: true },
  files: { label: 'Новые версии файлов', hint: 'Кто-то загрузил новую версию документа' },
  reminders: { label: 'Напоминания о сроках', hint: 'Скоро срок, срок истёк, задача ждёт решения, утренняя сводка' },
  general: { label: 'Объявления и замещения', hint: 'Рассылки администратора, назначение заместителем' },
} as const;
export type NotificationCategory = keyof typeof CATEGORIES;
/** noEmail — по этой категории письма не отправляются вовсе (только колокольчик и устройства). */
export const NOTIFICATION_CATEGORIES: Record<NotificationCategory, { label: string; hint: string; noEmail?: boolean }> = CATEGORIES;
export const NOTIFICATION_CATEGORY_KEYS = Object.keys(CATEGORIES) as NotificationCategory[];

const BY_KIND: Record<string, NotificationCategory> = {
  'approval.pending': 'approvals',
  'approval.task': 'approvals',
  'approval.control': 'approvals',
  'approval.approved': 'results',
  'approval.rejected': 'results',
  'approval.returned': 'results',
  'approval.declined': 'results',
  'approval.task_done': 'results',
  'approval.interrupted': 'results',
  'approval.unresolved': 'results',
  'approval.observer': 'results',
  'route.notify': 'results',
  'task.assigned': 'assignments',
  'task.answered': 'assignments',
  'task.accepted': 'assignments',
  'task.reopened': 'assignments',
  'task.cancelled': 'assignments',
  'task.subtask_created': 'assignments',
  'regulation.run': 'assignments',
  'task.comment': 'comments',
  'file.new_version': 'files',
};

/** Категория по виду уведомления; неизвестные — в «Объявления и замещения». */
export function notificationCategory(kind: string): NotificationCategory {
  if (kind.startsWith('reminder.')) return 'reminders';
  return BY_KIND[kind] ?? 'general';
}

const toggles = z.partialRecord(z.enum(NOTIFICATION_CATEGORY_KEYS as [NotificationCategory, ...NotificationCategory[]]), z.boolean());
/** Отключённые категории; чего нет — включено. */
export const notificationPrefsSchema = z.object({ push: toggles.default({}), email: toggles.default({}) });
export type NotificationPrefs = z.output<typeof notificationPrefsSchema>;

/** Включена ли категория в канале (по умолчанию — да). */
export function prefEnabled(prefs: NotificationPrefs, channel: 'push' | 'email', kind: string): boolean {
  return prefs[channel][notificationCategory(kind)] !== false;
}

/** Подписка браузера (PushSubscription.toJSON()). */
export const pushSubscriptionSchema = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({ p256dh: z.string().min(10).max(200), auth: z.string().min(4).max(100) }),
});
export type PushSubscriptionInput = z.input<typeof pushSubscriptionSchema>;

export interface PushDeviceDto {
  id: string;
  /** «Chrome, Android» — по User-Agent. */
  label: string;
  createdAt: string;
  lastSuccessAt: string | null;
}

export interface NotificationSettingsDto {
  prefs: NotificationPrefs;
  devices: PushDeviceDto[];
  /** Открытый ключ VAPID для pushManager.subscribe. */
  publicKey: string;
}

/** Что сервер кладёт в push-сообщение; его читает service worker (public/sw.js). */
export interface PushPayload {
  id: string | null;
  title: string;
  body: string | null;
  url: string;
  tag: string;
  /** Непрочитанных у сотрудника — для значка на иконке приложения. */
  unread: number;
}
